import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '@/services/api';
import { 
  Users, 
  Crown, 
  UserPlus, 
  Activity, 
  TrendingUp,
  Shield,
  Calendar,
  RefreshCw,
  AlertCircle,
  ChevronRight,
  BookOpen,
  CreditCard,
  Newspaper,
  Zap,
  LogIn,
  Rocket,
  Download
} from 'lucide-react';

// Admin emails that can access this page
const ADMIN_EMAILS = ['hariravi28@gmail.com', 'venkiganesh903@gmail.com'];

export default function AdminDashboard() {
  const { user, token, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const [userStats, setUserStats] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [activityStats, setActivityStats] = useState(null);
  const [revenueStats, setRevenueStats] = useState(null);
  const [siPurchases, setSiPurchases] = useState(null);
  const [intelligenceStats, setIntelligenceStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshingIntelligence, setRefreshingIntelligence] = useState(false);
  const [syncBusy, setSyncBusy] = useState(null);        // 'csat' | 'heatmap' | null
  const [syncMsg, setSyncMsg] = useState(null);
  const [csatSync, setCsatSync] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [authError, setAuthError] = useState(null);

  const [exportingCsv, setExportingCsv] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [usersData, setUsersData] = useState({ users: [], total: 0, total_pages: 1, page: 1 });
  const [usersLoading, setUsersLoading] = useState(false);

  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  const fetchUsersList = useCallback(async (page, search) => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '25', search: search || '' });
      const res = await fetch(`${API_BASE}/admin/stats/users-list?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setUsersData(await res.json());
      }
    } catch (e) {
      // keep previous data
    } finally {
      setUsersLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAdmin || !token) return;
    fetchUsersList(userPage, userSearch);
  }, [userPage]);

  useEffect(() => {
    if (!isAdmin || !token) return;
    const t = setTimeout(() => {
      setUserPage(1);
      fetchUsersList(1, userSearch);
    }, 350);
    return () => clearTimeout(t);
  }, [userSearch]);

  const downloadUsersCSV = async () => {
    setExportingCsv(true);
    try {
      const res = await fetch(`${API_BASE}/admin/stats/users-export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'clarias_users.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Could not export users CSV. Please try again.');
    } finally {
      setExportingCsv(false);
    }
  };

  // Auto-refresh token and validate session on page load
  const validateAndRefreshAuth = useCallback(async () => {
    setAuthChecking(true);
    setAuthError(null);
    
    // Check if token exists
    if (!token) {
      setAuthError('no_token');
      setAuthChecking(false);
      return false;
    }
    
    try {
      // Verify token with backend
      const response = await fetch(`${API_BASE}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 401) {
        // Token expired or invalid
        setAuthError('token_expired');
        setAuthChecking(false);
        return false;
      }
      
      if (!response.ok) {
        setAuthError('server_error');
        setAuthChecking(false);
        return false;
      }
      
      const userData = await response.json();
      
      // Check if user is admin
      if (!ADMIN_EMAILS.includes(userData.email?.toLowerCase())) {
        setAuthError('not_admin');
        setAuthChecking(false);
        return false;
      }
      
      // Refresh user data in context if available
      if (refreshUser) {
        await refreshUser();
      }
      
      setAuthChecking(false);
      return true;
    } catch (err) {
      console.error('Auth validation error:', err);
      setAuthError('network_error');
      setAuthChecking(false);
      return false;
    }
  }, [token, refreshUser]);

  // Handle login redirect
  const handleLoginRedirect = () => {
    // Clear any stale auth data
    if (logout) {
      logout();
    }
    navigate('/login', { state: { from: '/admin', message: 'Please login as admin to continue' } });
  };

  // Validate auth on mount
  useEffect(() => {
    validateAndRefreshAuth().then(isValid => {
      if (isValid) {
        fetchAllStats();
      }
    });
  }, [validateAndRefreshAuth]);

  const triggerIntelligenceRefresh = async () => {
    setRefreshingIntelligence(true);
    try {
      const res = await fetch(`${API_BASE}/admin/trigger-intelligence`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        alert('Intelligence generation triggered! This may take 1-2 minutes. Refresh the page to see updated data.');
      } else {
        alert('Failed to trigger intelligence refresh');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setRefreshingIntelligence(false);
    }
  };

  // ── Production content sync (one-click, admin-JWT gated) ──
  const pollCsatStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/universal-bank/admin/sync-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { const d = await res.json(); setCsatSync(d.state); return d.state; }
    } catch (e) { /* ignore */ }
    return null;
  }, [token]);

  const syncCsatBank = async () => {
    setSyncBusy('csat');
    setCsatSync(null);
    setSyncMsg('Starting CSAT bank sync… (runs in the background)');
    try {
      // Fire-and-forget background job — returns instantly, so it never hits
      // Cloudflare's request timeout. We then poll status for live progress.
      const res = await fetch(`${API_BASE}/universal-bank/admin/sync-csat-bank`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      const d = await res.json();
      if (!res.ok) { setSyncMsg('Failed: ' + (d.detail || res.status)); setSyncBusy(null); return; }
      const iv = setInterval(async () => {
        const s = await pollCsatStatus();
        if (!s) return;
        if (s.running) {
          setSyncMsg(`Syncing… fetched ${s.fetched || 0} · ${s.inserted || 0} new · ${s.updated || 0} updated`);
        } else {
          clearInterval(iv);
          setSyncBusy(null);
          setSyncMsg(s.error
            ? `Stopped: ${s.error} — click again to resume`
            : `Done ✓  Bank total: ${s.bank_total}${s.failed_batches ? `  ·  ${s.failed_batches} chunk(s) skipped — click again to backfill` : ''}`);
        }
      }, 3000);
    } catch (e) { setSyncMsg('Error: ' + e.message); setSyncBusy(null); }
  };

  const syncHeatmaps = async () => {
    setSyncBusy('heatmap');
    setSyncMsg('Syncing Topic Heatmaps…');
    try {
      const res = await fetch(`${API_BASE}/prelims/admin/sync-heatmaps`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      const d = await res.json();
      if (!res.ok) setSyncMsg('Failed: ' + (d.detail || res.status));
      else setSyncMsg(`Heatmaps synced ✓  ${d.cache_total} cached (UPSC + SI Telangana/Andhra + states)`);
    } catch (e) { setSyncMsg('Error: ' + e.message); }
    finally { setSyncBusy(null); }
  };

  const fetchAllStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch all stats with individual error handling
      const [usersRes, recentRes, activityRes, revenueRes, intelligenceRes, siRes] = await Promise.all([
        fetch(`${API_BASE}/admin/stats/users`, { headers }).catch(e => ({ ok: false, error: e.message })),
        fetch(`${API_BASE}/admin/stats/recent-users?limit=10`, { headers }).catch(e => ({ ok: false, error: e.message })),
        fetch(`${API_BASE}/admin/stats/activity?days=7`, { headers }).catch(e => ({ ok: false, error: e.message })),
        fetch(`${API_BASE}/admin/stats/revenue`, { headers }).catch(e => ({ ok: false, error: e.message })),
        fetch(`${API_BASE}/admin/stats/intelligence`, { headers }).catch(e => ({ ok: false, error: e.message })),
        fetch(`${API_BASE}/admin/si-purchases`, { headers }).catch(e => ({ ok: false, error: e.message }))
      ]);

      // Check for auth errors
      if (usersRes.status === 401 || usersRes.status === 403) {
        throw new Error('Session expired or not logged in. Please log out and log back in as admin.');
      }
      
      if (usersRes.status === 404) {
        throw new Error('Admin API not found. The backend may need to be redeployed.');
      }
      
      if (!usersRes.ok) {
        const errorData = await usersRes.json().catch(() => ({}));
        throw new Error(errorData.detail || `API Error: ${usersRes.status}`);
      }
      
      const [users, recent, activity, revenue, intelligence, siData] = await Promise.all([
        usersRes.json(),
        recentRes.ok ? recentRes.json() : { users: [] },
        activityRes.ok ? activityRes.json() : { activity: {}, engagement: {} },
        revenueRes.ok ? revenueRes.json() : { subscriptions: {}, optionals_purchased: {} },
        intelligenceRes.ok ? intelligenceRes.json() : null,
        siRes.ok ? siRes.json() : { summary: {}, purchases: [] }
      ]);

      setUserStats(users);
      setRecentUsers(recent.users || []);
      setActivityStats(activity);
      setRevenueStats(revenue);
      setIntelligenceStats(intelligence);
      setSiPurchases(siData);
      setLastRefresh(new Date());
    } catch (err) {
      // If auth error, set auth error state
      if (err.message.includes('Session expired') || err.message.includes('401')) {
        setAuthError('token_expired');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth
  if (authChecking) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-spin" />
          <h2 className="text-xl text-white mb-2">Verifying Access...</h2>
          <p className="text-slate-400">Checking your admin credentials</p>
        </div>
      </div>
    );
  }

  // Show auth error states
  if (authError) {
    const errorMessages = {
      no_token: {
        icon: <LogIn className="w-16 h-16 text-orange-400 mx-auto mb-4" />,
        title: 'Login Required',
        message: 'You need to log in to access the admin dashboard.',
        buttonText: 'Go to Login',
        buttonAction: handleLoginRedirect
      },
      token_expired: {
        icon: <AlertCircle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />,
        title: 'Session Expired',
        message: 'Your session has expired. Please log in again to continue.',
        buttonText: 'Login Again',
        buttonAction: handleLoginRedirect
      },
      not_admin: {
        icon: <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />,
        title: 'Access Denied',
        message: 'You don\'t have admin privileges. Only authorized admins can access this page.',
        buttonText: 'Go to Dashboard',
        buttonAction: () => navigate('/dashboard')
      },
      network_error: {
        icon: <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />,
        title: 'Connection Error',
        message: 'Unable to connect to the server. Please check your internet connection.',
        buttonText: 'Try Again',
        buttonAction: () => window.location.reload()
      },
      server_error: {
        icon: <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />,
        title: 'Server Error',
        message: 'Something went wrong on our end. Please try again later.',
        buttonText: 'Try Again',
        buttonAction: () => window.location.reload()
      }
    };

    const errorConfig = errorMessages[authError] || errorMessages.server_error;

    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          {errorConfig.icon}
          <h2 className="text-2xl font-bold text-white mb-2">{errorConfig.title}</h2>
          <p className="text-slate-400 mb-6">{errorConfig.message}</p>
          <button 
            onClick={errorConfig.buttonAction}
            className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium transition-colors"
          >
            {errorConfig.buttonText}
          </button>
          {authError === 'token_expired' && (
            <p className="text-slate-500 text-sm mt-4">
              Tip: Make sure you're logging in with an admin email
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <LogIn className="w-16 h-16 text-orange-400 mx-auto mb-4" />
          <h2 className="text-xl text-white mb-2">Login Required</h2>
          <p className="text-slate-400 mb-4">Please log in to access the admin dashboard</p>
          <button 
            onClick={handleLoginRedirect}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl text-white mb-2">Access Denied</h2>
          <p className="text-slate-400 mb-4">You don't have permission to access this page</p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
              <Shield className="w-8 h-8 text-orange-500" />
              Admin Dashboard
            </h1>
            <p className="text-slate-400 mt-1">Monitor your platform's performance</p>
          </div>
          <div className="flex items-center gap-4 mt-4 md:mt-0">
            <button
              onClick={() => navigate('/admin/csat-concept-notes')}
              data-testid="nav-csat-concept-notes"
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <Rocket className="w-4 h-4" />
              CSAT Concept Notes
            </button>
            <button
              onClick={() => navigate('/admin/free-answer')}
              data-testid="nav-free-answer-funnel"
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              <Rocket className="w-4 h-4" />
              Free Answer Funnel
            </button>
            {lastRefresh && (
              <span className="text-sm text-slate-500">
                Last updated: {lastRefresh.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={fetchAllStats}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {loading && !userStats ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard
                icon={<Users className="w-6 h-6" />}
                label="Total Users"
                value={userStats?.total_users || 0}
                color="blue"
              />
              <StatCard
                icon={<Crown className="w-6 h-6" />}
                label="PRO Users"
                value={userStats?.breakdown?.pro_users || 0}
                subtitle={`${userStats?.breakdown?.pro_percentage || 0}%`}
                color="orange"
              />
              <StatCard
                icon={<UserPlus className="w-6 h-6" />}
                label="Signups Today"
                value={userStats?.signups?.today || 0}
                color="green"
              />
              <StatCard
                icon={<Calendar className="w-6 h-6" />}
                label="This Week"
                value={userStats?.signups?.this_week || 0}
                color="purple"
              />
            </div>

            {/* Production Content Sync — one-click */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50 mb-8" data-testid="admin-sync-panel">
              <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                <Rocket className="w-5 h-5 text-cyan-400" />
                Production Content Sync
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                Pull the latest CSAT/Aptitude question bank and Topic Heatmaps (UPSC + SI) from preview into this environment. Safe to run anytime — fully idempotent.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={syncCsatBank}
                  disabled={!!syncBusy}
                  data-testid="admin-sync-csat-btn"
                  className="flex items-center gap-2 px-4 py-2.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 font-medium"
                >
                  <RefreshCw className={`w-4 h-4 ${syncBusy === 'csat' ? 'animate-spin' : ''}`} />
                  {syncBusy === 'csat' ? 'Syncing CSAT Bank…' : 'Sync CSAT Question Bank'}
                </button>
                <button
                  onClick={syncHeatmaps}
                  disabled={!!syncBusy}
                  data-testid="admin-sync-heatmaps-btn"
                  className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
                >
                  <RefreshCw className={`w-4 h-4 ${syncBusy === 'heatmap' ? 'animate-spin' : ''}`} />
                  {syncBusy === 'heatmap' ? 'Syncing Heatmaps…' : 'Sync Topic Heatmaps (UPSC + SI)'}
                </button>
              </div>
              {syncMsg && (
                <p className="mt-3 text-sm text-slate-300" data-testid="admin-sync-status-msg">{syncMsg}</p>
              )}
              {csatSync && syncBusy === 'csat' && (
                <div className="mt-2 text-xs text-slate-400" data-testid="admin-sync-progress">
                  Progress: fetched {csatSync.fetched || 0} · inserted {csatSync.inserted || 0} · updated {csatSync.updated || 0}
                  {csatSync.failed_batches ? ` · ${csatSync.failed_batches} chunk(s) skipped` : ''}
                </div>
              )}
            </div>

            {/* Activity & Revenue Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Activity Stats */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-green-400" />
                  Activity (Last 7 Days)
                </h3>
                <div className="space-y-4">
                  <ActivityRow 
                    label="Answer Generations" 
                    value={activityStats?.activity?.answer_generations || 0} 
                  />
                  <ActivityRow 
                    label="MCQ Attempts" 
                    value={activityStats?.activity?.mcq_attempts || 0} 
                  />
                  <ActivityRow 
                    label="Test Attempts" 
                    value={activityStats?.activity?.test_attempts || 0} 
                  />
                  <ActivityRow 
                    label="Active Users" 
                    value={activityStats?.engagement?.active_users || 0}
                    highlight 
                  />
                </div>
              </div>

              {/* Revenue Stats */}
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-orange-400" />
                  Revenue Breakdown
                </h3>
                <div className="space-y-4">
                  <div className="text-sm text-slate-400 mb-2">Subscriptions</div>
                  {revenueStats?.subscriptions && Object.keys(revenueStats.subscriptions).length > 0 ? (
                    Object.entries(revenueStats.subscriptions).map(([plan, count]) => (
                      <ActivityRow key={plan} label={plan} value={count} />
                    ))
                  ) : (
                    <p className="text-slate-500 text-sm">No subscription data</p>
                  )}
                  
                  <div className="text-sm text-slate-400 mt-4 mb-2">Optionals Purchased</div>
                  {revenueStats?.optionals_purchased && Object.keys(revenueStats.optionals_purchased).length > 0 ? (
                    Object.entries(revenueStats.optionals_purchased).map(([opt, count]) => (
                      <ActivityRow key={opt} label={opt} value={count} />
                    ))
                  ) : (
                    <p className="text-slate-500 text-sm">No optional purchases</p>
                  )}
                </div>
              </div>
            </div>

            {/* SI (Sub-Inspector) Purchases */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50 mb-8" data-testid="admin-si-purchases">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-amber-400" />
                SI Plan Purchases
              </h3>

              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                {[
                  { label: "SI Members", value: siPurchases?.summary?.si_members ?? 0, testid: "si-members" },
                  { label: "Total Revenue", value: `₹${(siPurchases?.summary?.total_revenue_inr ?? 0).toLocaleString("en-IN")}`, testid: "si-revenue" },
                  { label: "Yearly (paid)", value: siPurchases?.summary?.yearly_purchases ?? 0, testid: "si-yearly" },
                  { label: "Monthly (paid)", value: siPurchases?.summary?.monthly_purchases ?? 0, testid: "si-monthly" },
                ].map((c) => (
                  <div key={c.label} className="bg-slate-900/60 rounded-lg p-4 border border-slate-700/50" data-testid={`admin-${c.testid}`}>
                    <div className="text-2xl font-bold text-amber-300">{c.value}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{c.label}</div>
                  </div>
                ))}
              </div>

              {/* Buyers table */}
              {siPurchases?.purchases && siPurchases.purchases.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="admin-si-table">
                    <thead>
                      <tr className="text-left text-slate-400 border-b border-slate-700/50">
                        <th className="py-2 pr-4 font-medium">Name</th>
                        <th className="py-2 pr-4 font-medium">Email</th>
                        <th className="py-2 pr-4 font-medium">Plan</th>
                        <th className="py-2 pr-4 font-medium">Amount</th>
                        <th className="py-2 pr-4 font-medium">Coupon</th>
                        <th className="py-2 pr-4 font-medium">Status</th>
                        <th className="py-2 pr-4 font-medium">Purchased</th>
                        <th className="py-2 pr-4 font-medium">Expires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {siPurchases.purchases.map((p, i) => (
                        <tr key={i} className="border-b border-slate-800/50 text-slate-200" data-testid={`admin-si-row-${i}`}>
                          <td className="py-2 pr-4">{p.name}</td>
                          <td className="py-2 pr-4 text-slate-400">{p.email}</td>
                          <td className="py-2 pr-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${p.plan === "Yearly" ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300"}`}>{p.plan}</span>
                          </td>
                          <td className="py-2 pr-4">₹{Number(p.amount_inr).toLocaleString("en-IN")}</td>
                          <td className="py-2 pr-4 text-slate-400">{p.coupon || "—"}</td>
                          <td className="py-2 pr-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${p.paid ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-600/40 text-slate-400"}`}>{p.paid ? "Paid" : (p.status || "pending")}</span>
                          </td>
                          <td className="py-2 pr-4 text-slate-400">{p.purchased_at ? String(p.purchased_at).slice(0, 10) : "—"}</td>
                          <td className="py-2 pr-4 text-slate-400">{p.expires_at ? String(p.expires_at).slice(0, 10) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No SI purchases yet.</p>
              )}
            </div>


            {/* Intelligence Status */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Newspaper className="w-5 h-5 text-cyan-400" />
                  Daily Intelligence Status
                </h3>
                <button
                  onClick={triggerIntelligenceRefresh}
                  disabled={refreshingIntelligence}
                  className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 text-sm"
                >
                  <Zap className={`w-4 h-4 ${refreshingIntelligence ? 'animate-pulse' : ''}`} />
                  {refreshingIntelligence ? 'Triggering...' : 'Refresh Intelligence'}
                </button>
              </div>
              {intelligenceStats ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <div className="text-sm text-slate-400">Today's Date</div>
                    <div className="text-lg font-semibold text-white">{intelligenceStats.today}</div>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <div className="text-sm text-slate-400">Has Today's Data</div>
                    <div className={`text-lg font-semibold ${intelligenceStats.has_today_data ? 'text-green-400' : 'text-red-400'}`}>
                      {intelligenceStats.has_today_data ? '✅ Yes' : '❌ No'}
                    </div>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <div className="text-sm text-slate-400">Items Today</div>
                    <div className="text-lg font-semibold text-white">{intelligenceStats.today_items_count}</div>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <div className="text-sm text-slate-400">Last Generated</div>
                    <div className="text-sm font-semibold text-white">
                      {intelligenceStats.latest_generated_at 
                        ? new Date(intelligenceStats.latest_generated_at).toLocaleString('en-IN')
                        : 'N/A'}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500">Loading intelligence stats...</p>
              )}
              {intelligenceStats?.recent_dates && (
                <div className="mt-4">
                  <div className="text-sm text-slate-400 mb-2">Recent Data Available:</div>
                  <div className="flex flex-wrap gap-2">
                    {intelligenceStats.recent_dates.map((date, i) => (
                      <span key={i} className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs">
                        {date}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* User Security Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard
                icon={<Shield className="w-6 h-6" />}
                label="With Password"
                value={userStats?.security?.users_with_password || 0}
                color="green"
                small
              />
              <StatCard
                icon={<Users className="w-6 h-6" />}
                label="OTP Only"
                value={userStats?.security?.otp_only_users || 0}
                color="yellow"
                small
              />
              <StatCard
                icon={<BookOpen className="w-6 h-6" />}
                label="With Optionals"
                value={userStats?.optionals?.users_with_optionals || 0}
                color="purple"
                small
              />
              <StatCard
                icon={<TrendingUp className="w-6 h-6" />}
                label="This Month"
                value={userStats?.signups?.this_month || 0}
                color="blue"
                small
              />
            </div>

            {/* Recent Users Table */}
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-6 border-b border-slate-700/50 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-blue-400" />
                    All Users
                  </h3>
                  <p className="text-xs text-slate-400 mt-1" data-testid="admin-users-total">
                    {usersData.total.toLocaleString()} total users
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    data-testid="admin-users-search"
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search name, email or phone…"
                    className="px-3 py-1.5 text-sm rounded-lg bg-slate-900/60 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 w-full md:w-64"
                  />
                  <button
                    data-testid="admin-export-users-csv"
                    onClick={downloadUsersCSV}
                    disabled={exportingCsv}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 border border-blue-500/30 transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    <Download className="w-4 h-4" />
                    {exportingCsv ? 'Exporting…' : 'Download CSV'}
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/80">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Optionals</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Password</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/50">
                    {usersData.users.map((user, idx) => (
                      <tr key={idx} className="hover:bg-slate-700/30 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-white">{user.name || 'No name'}</div>
                            <div className="text-sm text-slate-400">{user.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {user.phone ? (
                            <span data-testid={`admin-user-phone-${idx}`} className="text-sm text-slate-200 font-mono">{user.phone}</span>
                          ) : (
                            <span className="text-slate-500 text-sm">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.role === 'PRO' 
                              ? 'bg-orange-500/20 text-orange-400' 
                              : 'bg-slate-600/50 text-slate-300'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-300">
                          {new Date(user.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="px-6 py-4">
                          {user.optionals?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {user.optionals.map((opt, i) => (
                                <span key={i} className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded">
                                  {opt}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-500 text-sm">None</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex w-2 h-2 rounded-full ${
                            user.has_password ? 'bg-green-400' : 'bg-yellow-400'
                          }`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {usersData.users.length === 0 && (
                <div className="px-6 py-8 text-center text-slate-400 text-sm" data-testid="admin-users-empty">
                  {usersLoading ? 'Loading users…' : 'No users match your search.'}
                </div>
              )}
              {usersData.total_pages > 1 && (
                <div className="p-4 border-t border-slate-700/50 flex items-center justify-between">
                  <span className="text-xs text-slate-400">
                    Page {usersData.page} of {usersData.total_pages}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="admin-users-prev"
                      onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                      disabled={userPage <= 1 || usersLoading}
                      className="px-3 py-1.5 text-sm rounded-lg bg-slate-700/50 text-slate-200 hover:bg-slate-700 disabled:opacity-40"
                    >
                      Previous
                    </button>
                    <button
                      data-testid="admin-users-next"
                      onClick={() => setUserPage((p) => Math.min(usersData.total_pages, p + 1))}
                      disabled={userPage >= usersData.total_pages || usersLoading}
                      className="px-3 py-1.5 text-sm rounded-lg bg-slate-700/50 text-slate-200 hover:bg-slate-700 disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ icon, label, value, subtitle, color, small }) {
  const colors = {
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400',
    orange: 'from-orange-500/20 to-orange-600/10 border-orange-500/30 text-orange-400',
    green: 'from-green-500/20 to-green-600/10 border-green-500/30 text-green-400',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400',
    yellow: 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30 text-yellow-400',
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl ${small ? 'p-4' : 'p-6'}`}>
      <div className={`${colors[color].split(' ').pop()} mb-2`}>
        {icon}
      </div>
      <div className={`${small ? 'text-2xl' : 'text-3xl'} font-bold text-white`}>
        {value.toLocaleString()}
        {subtitle && <span className="text-sm font-normal text-slate-400 ml-2">{subtitle}</span>}
      </div>
      <div className="text-sm text-slate-400 mt-1">{label}</div>
    </div>
  );
}

// Activity Row Component
function ActivityRow({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-400">{label}</span>
      <span className={`font-semibold ${highlight ? 'text-green-400' : 'text-white'}`}>
        {value.toLocaleString()}
      </span>
    </div>
  );
}
